// Shared drag + QWERTY→Ottoman mapping for search bar and grid keyboards.
import { isOttomanText } from "../../lib/ottoman_text_utils"

// localStorage key for persisting Ottoman keyboard drag position across sessions
export const LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY = "lexiqamusSearchOttomanKeyboardPosition"
// Separate drag key for TypeO word edit — search bar clears its key on every page load.
export const TYPEO_OTTOMAN_KEYBOARD_DRAG_KEY = "typeoOttomanKeyboardPosition"

const FLOATING_KEYBOARD_DRAG_VIEWPORT_MARGIN_PX = 4

function clampLexiqamusDrag(n, min, max) {
  return Math.min(max, Math.max(min, n))
}

function setFloatingKeyboardPanelPosition(panelEl, left, top) {
  panelEl.style.left = `${left}px`
  panelEl.style.top = `${top}px`
  panelEl.style.right = "auto"
  panelEl.style.bottom = "auto"
  panelEl.style.transform = "none"
}

function clampFloatingKeyboardPanelPosition(panelEl, left, top, margin = FLOATING_KEYBOARD_DRAG_VIEWPORT_MARGIN_PX) {
  const { width, height } = panelEl.getBoundingClientRect()
  setFloatingKeyboardPanelPosition(
    panelEl,
    clampLexiqamusDrag(left, margin, window.innerWidth - width - margin),
    clampLexiqamusDrag(top, margin, window.innerHeight - height - margin)
  )
}

// Attaches drag functionality to a floating keyboard panel via a drag handle.
// Persists position to localStorage using the given storageKey (defaults to
// LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY for backward compatibility).
// options.onRestoreDefaultPosition — callback to restore default anchored position (Home key).
// Returns cleanup function to remove event listeners.
export function attachLexiqamusOttomanKeyboardDrag(panelEl, dragHandleEl, storageKey = LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY, options = {}) {
  if (!panelEl || !dragHandleEl) return () => {}

  let armed = false
  let dragging = false
  let offsetX = 0
  let offsetY = 0

  const capturePanelPositionFromViewport = () => {
    const { left, top } = panelEl.getBoundingClientRect()
    setFloatingKeyboardPanelPosition(panelEl, left, top)
  }

  // Saves current panel position to localStorage for persistence across sessions
  const persist = () => {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ top: panelEl.style.top, left: panelEl.style.left })
      )
    } catch (_e) {
      // ignore — localStorage may be unavailable (e.g. private browsing)
    }
  }

  // Updates panel position during drag, clamped to viewport bounds with margin
  const onPointerMove = (e) => {
    if (!armed || !dragging) return
    e.preventDefault()
    const panelWidth  = panelEl.offsetWidth
    const panelHeight = panelEl.offsetHeight
    const margin = FLOATING_KEYBOARD_DRAG_VIEWPORT_MARGIN_PX
    const left = clampLexiqamusDrag(e.clientX - offsetX, margin, window.innerWidth - panelWidth - margin)
    const top  = clampLexiqamusDrag(e.clientY - offsetY, margin, window.innerHeight - panelHeight - margin)
    setFloatingKeyboardPanelPosition(panelEl, left, top)
  }

  // Cleans up drag state and persists final position
  const endDrag = () => {
    if (!armed) return
    armed = false
    document.removeEventListener("pointermove", onPointerMove)
    document.removeEventListener("pointerup", onPointerUp)
    document.removeEventListener("pointercancel", onPointerUp)
    if (dragging) {
      panelEl.classList.remove("lexiqamus-search-keyboard--dragging")
      persist()
    }
    dragging = false
  }

  const onPointerUp = () => {
    endDrag()
  }

  // Initiates drag on left-click of drag handle (excludes action buttons)
  const onPointerDown = (e) => {
    if (e.button !== 0) return
    if (e.target.closest(".ottoman-keyboard-action-btn")) return
    if (e.target.closest("button")) return
    if (!dragHandleEl.contains(e.target)) return

    e.preventDefault()
    armed   = true
    dragging = true
    capturePanelPositionFromViewport()
    offsetX = e.clientX - panelEl.getBoundingClientRect().left
    offsetY = e.clientY - panelEl.getBoundingClientRect().top
    panelEl.classList.add("lexiqamus-search-keyboard--dragging")
    document.addEventListener("pointermove", onPointerMove, { passive: false })
    document.addEventListener("pointerup", onPointerUp, true)
    document.addEventListener("pointercancel", onPointerUp, true)
  }

  const onDragHandleKeydown = (e) => {
    if (e.target.closest("button")) return

    const { left, top } = panelEl.getBoundingClientRect()
    const step = e.shiftKey ? 48 : 12

    if (e.key === "ArrowLeft") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left - step, top)
      persist()
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left + step, top)
      persist()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left, top - step)
      persist()
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      clampFloatingKeyboardPanelPosition(panelEl, left, top + step)
      persist()
    } else if (e.key === "Home") {
      e.preventDefault()
      options.onRestoreDefaultPosition?.()
    } else if (e.altKey && ["1", "2", "3", "4"].includes(e.key)) {
      e.preventDefault()
      const dockMargin = 8
      const panelWidth = panelEl.offsetWidth
      const panelHeight = panelEl.offsetHeight
      const cornerPositions = {
        1: { left: dockMargin, top: dockMargin },
        2: { left: window.innerWidth - panelWidth - dockMargin, top: dockMargin },
        3: { left: dockMargin, top: window.innerHeight - panelHeight - dockMargin },
        4: { left: window.innerWidth - panelWidth - dockMargin, top: window.innerHeight - panelHeight - dockMargin }
      }
      const cornerPosition = cornerPositions[parseInt(e.key, 10)]
      if (cornerPosition) setFloatingKeyboardPanelPosition(panelEl, cornerPosition.left, cornerPosition.top)
      persist()
    }
  }

  dragHandleEl.addEventListener("pointerdown", onPointerDown)
  dragHandleEl.addEventListener("keydown", onDragHandleKeydown)

  return () => {
    dragHandleEl.removeEventListener("pointerdown", onPointerDown)
    dragHandleEl.removeEventListener("keydown", onDragHandleKeydown)
    endDrag()
  }
}

// Maps Turkish QWERTY key characters to Ottoman characters for Turkish locale.
// Uses event.key (actual typed character) rather than event.code (physical position)
// because Turkish keys (ı, ğ, ü, ş, ö, ç) differ from standard QWERTY positions.
// Supports Shift modifiers and ALT combinations.
export function ottomanCharForTurkishKey(key, shift, alt) {
  const k = key.toLowerCase()

  // ALT combinations override base key (same as English layout)
  if (alt && !shift) {
    const altMap = { z: "ذ", s: "ث", t: "ة" }
    return altMap[k] || null
  }

  // Shift variants
  if (shift && !alt) {
    const shiftMap = { t: "ط", a: "ء", s: "ص", d: "ض", h: "ح", z: "ظ", n: "ڭ" }
    return shiftMap[k] || null
  }

  // Base keys (no modifier)
  if (!shift && !alt) {
    const base = {
      q: "ق", w: "و", e: "ە", r: "ر", t: "ت",
      y: "ی", u: "و", ı: "ع", o: "و", p: "پ",
      ğ: "غ", ü: "و",
      a: "ا", s: "س", d: "د", f: "ف", g: "گ",
      h: "ه", j: "ژ", k: "ك", l: "ل", ş: "ش",
      i: "ی",
      z: "ز", x: "خ", c: "ج", v: "و", b: "ب",
      n: "ن", m: "م", ö: "و", ç: "چ"
    }
    return base[k] || null
  }

  return null
}

// Maps physical keyboard keys to Ottoman characters for LexiQamus Ottoman mode.
// Supports base keys, Shift modifiers, and Alt combinations (ALT+n).
// Returns Ottoman character or null if no mapping exists.
//
// Note: multiple Latin keys intentionally map to the same Ottoman character
// (e.g. U/O/V → و, Y/I → ی) — this mirrors the Ottoman phonetic layout.
export function ottomanCharForPhysicalKey(code, shift, alt) {
  // Multi-character keys with Shift and Alt variants
  switch (code) {
    case "KeyT":
      if (alt && !shift) return "ث"
      if (shift && !alt) return "ط"
      if (!shift && !alt) return "ت"
      return null
    case "KeyA":
      if (alt) return null
      return shift ? "ء" : "ا"
    case "KeyS":
      if (alt && !shift) return "ص"
      if (shift && !alt) return "ش"
      if (!shift && !alt) return "س"
      return null
    case "KeyD":
      if (alt) return null
      return shift ? "ض" : "د"
    case "KeyG":
      if (alt) return null
      return shift ? "غ" : "ج"
    case "KeyH":
      if (alt && !shift) return "ة"
      if (shift && !alt) return "ح"
      if (!shift && !alt) return "ه"
      return null
    case "KeyK":
      if (alt) return null
      return shift ? "گ" : "ك"
    case "KeyZ":
      if (alt && !shift) return "ذ"
      if (shift && !alt) return "ظ"
      if (!shift && !alt) return "ز"
      return null
    case "KeyN":
      if (alt) return null
      return shift ? "ڭ" : "ن"
    default:
      break
  }

  // Single-character keys (no Shift/Alt variants)
  if (alt || shift) return null

  const singles = {
    KeyQ: "ق",
    KeyW: "و",
    KeyE: "ع",
    KeyR: "ر",
    KeyY: "ی",
    KeyU: "و",
    KeyI: "ی",
    KeyO: "و",
    KeyP: "پ",
    KeyF: "ف",
    KeyJ: "ژ",
    KeyL: "ل",
    KeyX: "خ",
    KeyC: "چ",
    KeyV: "و",
    KeyB: "ب",
    KeyM: "م"
  }
  return singles[code] || null
}

const OTTOMAN_KEYDOWN_PASSTHROUGH_KEYS = [
  // Navigation, editing, and submit keys — never mapped to Ottoman characters.
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Tab",
  "Enter",
  "Escape"
]

// Shared by Lexiqamus search bar and TypeO typing — returns null when the browser should handle the key.
export function resolveOttomanCharFromKeydown(event, isTurkish) {
  if (event.ctrlKey || event.metaKey) return null
  if (event.isComposing) return null
  if (OTTOMAN_KEYDOWN_PASSTHROUGH_KEYS.includes(event.key)) return null

  // Script-producing physical keyboards (Osmanlıca, Arabic, Urdu) already supply the
  // correct Ottoman/Arabic character in event.key — do not replace with LexiQamus
  // on-screen layout (e.g. Osmanlıca KeyC → ج must not become LexiQamus KeyC → چ).
  // Refrence ticket: https://evolving-bits.atlassian.net/jira/software/projects/CPG/boards/1?selectedIssue=CPG-179
  if (event.key.length === 1 && isOttomanText(event.key)) return null

  if (isTurkish) {
    return ottomanCharForTurkishKey(event.key, event.shiftKey, event.altKey)
  }
  return ottomanCharForPhysicalKey(event.code, event.shiftKey, event.altKey)
}

// Visible, focusable on-screen keyboard controls inside root (skips hidden decoder panels).
function queryVisibleKeyboardControls(root, selector) {
  if (!root) return []

  return [...root.querySelectorAll(selector)].filter((keyboardElement) => {
    if (keyboardElement.disabled || keyboardElement.getAttribute("aria-hidden") === "true") return false
    if (keyboardElement.closest(".lq-gk-panel--hidden-left, .lq-gk-panel--hidden-right")) return false
    const elementStyle = window.getComputedStyle(keyboardElement)
    return elementStyle.display !== "none" && elementStyle.visibility !== "hidden"
  })
}

function resolveFocusedKeyboardControl(focusedElement, rootElement, controlSelector) {
  if (!focusedElement || !rootElement.contains(focusedElement)) return null
  if (focusedElement.matches(controlSelector)) return focusedElement
  const closestMatchingControl = focusedElement.closest(controlSelector)
  return closestMatchingControl && rootElement.contains(closestMatchingControl) ? closestMatchingControl : null
}

// Move focus to the nearest key in the arrow direction (spatial nav for irregular grids).
function moveKeyboardFocusInDirection(focusedEl, candidates, direction) {
  if (!focusedEl || candidates.length === 0) return false

  const currentElementRect = focusedEl.getBoundingClientRect()
  const currentCenterX = currentElementRect.left + currentElementRect.width / 2
  const currentCenterY = currentElementRect.top + currentElementRect.height / 2
  let closestElement = null
  let closestElementScore = Infinity

  for (const candidateElement of candidates) {
    if (candidateElement === focusedEl) continue

    const candidateRect = candidateElement.getBoundingClientRect()
    const candidateCenterX = candidateRect.left + candidateRect.width / 2
    const candidateCenterY = candidateRect.top + candidateRect.height / 2
    const deltaX = candidateCenterX - currentCenterX
    const deltaY = candidateCenterY - currentCenterY

    if (direction === "ArrowLeft" && deltaX >= -4) continue
    if (direction === "ArrowRight" && deltaX <= 4) continue
    if (direction === "ArrowUp" && deltaY >= -4) continue
    if (direction === "ArrowDown" && deltaY <= 4) continue

    const primaryAxisDistance = direction === "ArrowLeft" || direction === "ArrowRight" ? Math.abs(deltaX) : Math.abs(deltaY)
    const secondaryAxisDistance = direction === "ArrowLeft" || direction === "ArrowRight" ? Math.abs(deltaY) : Math.abs(deltaX)
    const navigationScore = primaryAxisDistance + secondaryAxisDistance * 2
    if (navigationScore < closestElementScore) {
      closestElementScore = navigationScore
      closestElement = candidateElement
    }
  }

  if (!closestElement) return false
  closestElement.focus()
  return true
}

// Returns true when an arrow key moved focus between keys.
export function navigateOnscreenKeyboardWithArrowKeys(event, keyboardRoot, keyControlSelector) {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return false

  const currentlyFocusedControl = resolveFocusedKeyboardControl(document.activeElement, keyboardRoot, keyControlSelector)
  if (!currentlyFocusedControl) return false

  const allVisibleKeyControls = queryVisibleKeyboardControls(keyboardRoot, keyControlSelector)
  if (!allVisibleKeyControls.includes(currentlyFocusedControl)) return false

  event.preventDefault()
  return moveKeyboardFocusInDirection(currentlyFocusedControl, allVisibleKeyControls, event.key)
}

// Shared locale-aware mapper for search bar and Word Decoder physical keyboard input.
export function mapLexiqamusKeyboardEventToOttomanChar({ key, code, shiftKey, altKey, isTurkishLocale }) {
  return isTurkishLocale
    ? ottomanCharForTurkishKey(key, shiftKey, altKey)
    : ottomanCharForPhysicalKey(code, shiftKey, altKey)
}

// Self-check for CPG-416: Turkish ç/c and English KeyC map the same in search bar and Word Decoder.
export function assertLexiqamusOttomanKeyboardMappings() {
  const map = (key, code, isTurkishLocale) =>
    mapLexiqamusKeyboardEventToOttomanChar({ key, code, shiftKey: false, altKey: false, isTurkishLocale })

  if (map("ç", "KeyC", true) !== "چ") throw new Error("TR ç → چ")
  if (map("c", "KeyC", true) !== "ج") throw new Error("TR c → ج")
  if (map("c", "KeyC", false) !== "چ") throw new Error("EN KeyC → چ")
}
